import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Plus, Pencil, Trash2, Loader2, ExternalLink } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { useToast } from "@/hooks/use-toast";
import AdminLayout, { AdminPageHeader, AdminTable } from "./layout";

const basePath = import.meta.env.BASE_URL.replace(/\/$/, "");

interface Resource {
  id: number;
  title: string;
  organisation: string;
  url: string;
  category: string;
  audience: string;
  description: string | null;
  createdAt: string;
}

type RForm = Omit<Resource, "id" | "createdAt">;

const EMPTY: RForm = { title: "", organisation: "", url: "", category: "", audience: "patient", description: "" };

async function apiFetch(path: string, opts?: RequestInit) {
  const res = await fetch(`${basePath}${path}`, { credentials: "include", ...opts });
  if (!res.ok) { const b = await res.json().catch(() => ({})); throw new Error((b as { error?: string }).error ?? "Request failed"); }
  return res.json();
}

function RForm({ value, onChange }: { value: RForm; onChange: (v: RForm) => void }) {
  const set = (k: string, v: unknown) => onChange({ ...value, [k]: v });
  return (
    <div className="space-y-3">
      <div>
        <Label>Title *</Label>
        <Input value={value.title} onChange={(e) => set("title", e.target.value)} placeholder="Resource title" />
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label>Organisation *</Label>
          <Input value={value.organisation} onChange={(e) => set("organisation", e.target.value)} placeholder="e.g. NHS" />
        </div>
        <div>
          <Label>Audience *</Label>
          <Input value={value.audience} onChange={(e) => set("audience", e.target.value)} placeholder="patient / nurse / both" />
        </div>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label>Category *</Label>
          <Input value={value.category} onChange={(e) => set("category", e.target.value)} placeholder="e.g. Support" />
        </div>
        <div>
          <Label>URL *</Label>
          <Input value={value.url} onChange={(e) => set("url", e.target.value)} type="url" placeholder="https://…" />
        </div>
      </div>
      <div>
        <Label>Description</Label>
        <Textarea rows={2} value={value.description ?? ""} onChange={(e) => set("description", e.target.value)} />
      </div>
    </div>
  );
}

export default function AdminResources() {
  const qc = useQueryClient();
  const { toast } = useToast();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteId, setDeleteId] = useState<number | null>(null);
  const [editing, setEditing] = useState<Resource | null>(null);
  const [form, setForm] = useState<RForm>(EMPTY);

  const { data: resources = [], isLoading } = useQuery<Resource[]>({
    queryKey: ["admin", "resources"],
    queryFn: () => apiFetch("/api/admin/content/resources"),
  });

  const saveMutation = useMutation({
    mutationFn: (body: RForm) =>
      editing
        ? apiFetch(`/api/admin/content/resources/${editing.id}`, { method: "PUT", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body) })
        : apiFetch("/api/admin/content/resources", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body) }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["admin", "resources"] }); setDialogOpen(false); toast({ title: editing ? "Resource updated" : "Resource created" }); },
    onError: (err: Error) => toast({ title: "Error", description: err.message, variant: "destructive" }),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: number) => apiFetch(`/api/admin/content/resources/${id}`, { method: "DELETE" }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["admin", "resources"] }); toast({ title: "Resource deleted" }); },
    onError: (err: Error) => toast({ title: "Error", description: err.message, variant: "destructive" }),
  });

  function openCreate() { setEditing(null); setForm(EMPTY); setDialogOpen(true); }
  function openEdit(r: Resource) {
    setEditing(r);
    setForm({ title: r.title, organisation: r.organisation, url: r.url, category: r.category, audience: r.audience, description: r.description });
    setDialogOpen(true);
  }

  return (
    <AdminLayout>
      <AdminPageHeader
        title="Resources"
        description={`${resources.length} resource${resources.length !== 1 ? "s" : ""}`}
        action={<Button size="sm" onClick={openCreate}><Plus className="h-4 w-4 mr-2" />New resource</Button>}
      />

      {isLoading ? (
        <div className="flex items-center gap-2 text-slate-500"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</div>
      ) : (
        <AdminTable
          columns={["Title", "Organisation", "Audience", "Actions"]}
          rows={resources.map((r) => [
            <div key="title">
              <p className="font-medium text-slate-900 line-clamp-1">{r.title}</p>
              <a href={r.url} target="_blank" rel="noopener noreferrer" className="text-xs text-teal-600 flex items-center gap-1 hover:underline"><ExternalLink className="h-3 w-3" />Visit</a>
            </div>,
            <Badge key="org" variant="outline" className="text-xs">{r.organisation}</Badge>,
            <span key="aud" className="text-sm text-slate-600">{r.audience}</span>,
            <div key="actions" className="flex items-center gap-2">
              <Button size="icon" variant="ghost" onClick={() => openEdit(r)}><Pencil className="h-4 w-4" /></Button>
              <Button size="icon" variant="ghost" className="text-red-500 hover:text-red-700" onClick={() => setDeleteId(r.id)}><Trash2 className="h-4 w-4" /></Button>
            </div>,
          ])}
        />
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-xl">
          <DialogHeader><DialogTitle>{editing ? "Edit resource" : "New resource"}</DialogTitle></DialogHeader>
          <RForm value={form} onChange={setForm} />
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button onClick={() => saveMutation.mutate(form)} disabled={saveMutation.isPending}>
              {saveMutation.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              {editing ? "Save changes" : "Create"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={deleteId !== null} onOpenChange={(o) => !o && setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader><AlertDialogTitle>Delete resource?</AlertDialogTitle><AlertDialogDescription>This will permanently remove the resource.</AlertDialogDescription></AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction className="bg-red-600 hover:bg-red-700" onClick={() => { if (deleteId) { deleteMutation.mutate(deleteId); setDeleteId(null); } }}>Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </AdminLayout>
  );
}
